/**
 * Hold to talk's way there and back (SEO_BRIEF_10 section 2.4): a held clip goes through the
 * site's own upload and job, exactly as a recording on /voice-translator-to-english does, and
 * the translation is read off the transcript it makes.
 *
 *   the clip  → lib/upload-offer.ts `send`      the same size cap, the same refusals in the page's
 *                                               banner region, the minutes dialog (E10); the spoken
 *                                               language and `translate_to` sent with it
 *             → lib/autosub.ts waitForTranscript  the job polled at the server's pace, or the
 *                                               transcript at once from the cache
 *             → GET /api/transcripts/{id}        the words, read from its segments
 *
 * Into English the job translates the speech itself; into any other language it transcribes and
 * then translates, and a transcript with the `translation_unavailable` warning is the original,
 * left so because the language model had no room (the caller then shows the limit's sentence).
 * A translation that is not into English keeps its original transcript's words too
 * (`translation.original_public_id`), for the line's small original.
 *
 * The upload counts `translate_start` as every recording translated does (lib/translate-target.ts),
 * and this counts its `translate_done` when the job ends, as the job page would have.
 */

import { getTranscript } from '../api.ts';
import type { JobResponse, SubmitResponse, TranscriptDocument } from '../api-types.ts';
import { waitForTranscript } from '../autosub.ts';
import { track } from '../events.ts';
import { recordingName } from '../recorder.ts';
import { showState, uploadVariant } from '../states.ts';
import { countTranslateDone } from '../translate-target.ts';
import { createUploadOffer } from '../upload-offer.ts';
import type { HoldAnswer, HoldClip } from './core.ts';

/** The words of a transcript, its segments joined with a space. */
export function transcriptWords(doc: Pick<TranscriptDocument, 'segments'>): string {
  return doc.segments
    .map((segment) => segment.text.trim())
    .filter((text) => text !== '')
    .join(' ');
}

export interface ClipSenderDeps {
  /** The page's banner region, where the upload's refusals show (`#banner-region`). */
  region: HTMLElement;
  /** The page's slug, for the usage counter. */
  tool: string;
}

/** `sendClip` for the translator: one clip there and its translation back. */
export function createClipSender(deps: ClipSenderDeps): (clip: HoldClip) => Promise<HoldAnswer> {
  const { region } = deps;

  async function follow(response: SubmitResponse): Promise<HoldAnswer> {
    let engine = response.translate?.engine ?? null;
    let last: JobResponse | null = null;
    const wait = waitForTranscript(response, (job) => {
      last = job;
      if (job.translate?.engine) engine = job.translate.engine;
    });
    const result = await wait.done;
    const job = last as JobResponse | null;
    if (!response.resolved && job?.status === 'done') {
      countTranslateDone(response.job_id, job.translate);
    }
    if (!result.ok) {
      // The job page's states: E6 when nothing was said, E12 when the job failed, refunded.
      showState(region, uploadVariant(result.state));
      track('error_state', { state: result.state });
      return { ok: false };
    }
    const doc = await getTranscript(result.publicId);
    const text = transcriptWords(doc);
    const unavailable = doc.warnings.includes('translation_unavailable');
    let original: string | null = null;
    const originalId = doc.translation?.original_public_id;
    if (!unavailable && originalId) {
      try {
        original = transcriptWords(await getTranscript(originalId)) || null;
      } catch {
        /* the line shows the translation alone */
      }
    }
    return {
      ok: true,
      text,
      original,
      engine: engine === 'gemini' ? 'gemini' : 'whisper',
      unavailable,
    };
  }

  return (clip) =>
    new Promise<HoldAnswer>((resolve) => {
      let taken = false;
      const offer = createUploadOffer({
        region,
        tool: deps.tool,
        // The speaker's language is known: the transcription is told it rather than guessing.
        language: clip.from,
        translateTo: () => clip.to,
        onSubmitted: (response) => {
          taken = true;
          follow(response).then(resolve, () => {
            // The transcript could not be read back: the network's banner, as a failed poll's.
            showState(region, 'NETWORK');
            resolve({ ok: false });
          });
        },
      });
      const { recording } = clip;
      const file = new File([recording.blob], recordingName(recording.extension), {
        type: recording.blob.type || recording.type,
      });
      offer.send(file, recording.seconds).then(
        (ok) => {
          if (!ok && !taken) resolve({ ok: false });
        },
        () => {
          if (!taken) resolve({ ok: false });
        },
      );
    });
}
